import {FilterSuffix} from '@stencil/hotels-types/src/srp-types';
import {isValidArray, toCamelCase} from '@stencil/hotels-utils/src/common-utils';
import queryString from 'query-string'; // Install this package using: npm install query-string

// URL Structure  = FILTER_GROUP_1|FILTER_1,FILTER_2,FILTER_3,...|FILTER_TYPE^FILTER_GROUP_2...
const getFilterName = filter => {
  const {min, value, id_type, n} = filter || {};

  if (min !== undefined) {
    return `${min}-${filter.max}`;
  } else if (value !== undefined) {
    return `${value}`;
  } else if (id_type !== undefined) {
    return `${n}`;
  }
  return '';
};

const getFilterStringOmniture = ({filterArr, key}) => {
  const {min, value, id_type} = filterArr?.[0] || {};
  const isRangeFilter = min !== undefined;
  const isValueFilter = value !== undefined;
  const isLocationFilter = id_type !== undefined;
  if (isRangeFilter) {
    return filterArr
      .map(
        filter =>
          `Filters_${toCamelCase(key)} - ${getFilterName(filter)} : ${
            filter?.suffix || FilterSuffix.DEFAULT
          }`,
      )
      .join('|');
  } else if (isValueFilter) {
    return filterArr
      .map(
        filter =>
          `Filters_${toCamelCase(key)} - ${getFilterName(filter)} : ${
            filter?.suffix || FilterSuffix.DEFAULT
          }`,
      )
      .join('|');
  }
  if (isLocationFilter) {
    return filterArr.map(filter => `${getFilterName(filter)}`).join(':');
  } else return '';
};

const getFilterString = ({filterArr, key}) => {
  const {min, value, id_type} = filterArr?.[0] || {};
  const isRangeFilter = min !== undefined;
  const isValueFilter = value !== undefined;
  const isLocationFilter = id_type !== undefined;
  let filterRanges;
  if (isRangeFilter) {
    filterRanges = filterArr.map(filter => `${getFilterName(filter)}`).join(',');
    return `${key}|${filterRanges}|R`;
  } else if (isValueFilter) {
    filterRanges = filterArr.map(filter => filter.value).join(',');
    return `${key}|${filterRanges}`;
  } else if (isLocationFilter) {
    filterRanges = filterArr
      .map(
        filter =>
          `${filter?.n}$${filter?.loc_id}${filter?.la ? `$${filter.la}$${filter?.lo}` : ''}`,
      )
      .join(',');
    return `${key}|${filterRanges}|L`;
  } else return '';
};

const getFilterGroupArray = groupString => {
  const groupParts = groupString.split('|');
  const filterName = groupParts?.[0];
  const filterValues = groupParts?.[1]?.split(',');
  const isRangeFilter = groupParts?.[2] === 'R';
  const isLocationFilter = groupParts?.[2] === 'L';

  if (isRangeFilter) {
    return filterValues?.map(rangeString => {
      const [min, max] = rangeString?.split('-').map(Number);
      return {min, max};
    });
  }

  if (isLocationFilter) {
    return filterValues?.map(rangeString => {
      const [n, loc_id, la, lo] = rangeString?.split('$');
      const locObj = {n, loc_id, id_type: filterName};
      if (!!la) {
        locObj['la'] = Number(la);
        locObj['lo'] = Number(lo);
      }
      return locObj;
    });
  }

  return filterValues?.map(value => ({value: value?.trim()}));
};

export const parseFilterData = (filterData?: string) => {
  const appliedFilters = {};

  if (filterData) {
    const filterGroups = filterData?.split('^');

    filterGroups?.forEach(groupString => {
      const groupParts = groupString?.split('|');
      const filterName = groupParts?.[0];
      const filterArray = getFilterGroupArray(groupString);
      if (!!filterName && filterArray?.length > 0) {
        appliedFilters[filterName] = filterArray;
      }
    });
  }
  appliedFilters['isFilterCall'] = false;
  appliedFilters['isApplyCall'] = false;
  return appliedFilters;
};
/**
 * Extracts filterData from the URL query string
 * @param {string} search - The query string (from window.location.search or similar)
 * @returns {string | null} - The value of the filterData parameter or null if not found
 */
export const extractFilterData = search => {
  const parsed = queryString.parse(search);
  return (parsed.filterData as string) || null;
};

export const convertAppliedFiltertoStr = (filterObject: any) => {
  const appliedFilters: string[] = [];
  Object.keys(filterObject)?.forEach(key => {
    if (filterObject[key]?.length > 0) {
      const appliedFilterValue = getFilterString({filterArr: filterObject[key], key});
      appliedFilters.push(appliedFilterValue);
    }
  });
  return appliedFilters?.join('^');
};

/**
 * A helper function to create custom data for filters.
 *
 * @param {Object} appliedFilters - The filters object to be converted.
 * @param {Object} [additionalData={}] - Any additional key-value pairs you want to merge.
 * @returns {Object} - The resulting custom data object containing `newFilterData` and any additional data.
 */
export function buildFilterCustomData(appliedFilters, additionalData = {}) {
  const newFilterData = convertAppliedFiltertoStr(appliedFilters);
  // Build up the customData object, adding on any new properties from additionalData
  const customData = {
    ...additionalData,
    newFilterData: newFilterData || '',
  };

  return customData;
}

const getOmnitureAppliedFilter = (filterObject: any) => {
  const appliedFilters: string[] = [];
  Object.keys(filterObject)?.forEach(key => {
    if (filterObject[key]?.length > 0) {
      const appliedFilterValue = getFilterStringOmniture({
        filterArr: filterObject[key],
        key,
      });
      appliedFilters.push(appliedFilterValue);
    }
  });
  return appliedFilters?.join('|') || '';
};

const getOmnitureCombinedFilters = appliedFilters => {
  const combinedFilters = {};
  Object.entries(appliedFilters).forEach(([key, value]) => {
    if (isValidArray(value)) {
      value.forEach(filter => {
        if (!filter?.category) return;
        const category = filter.category.replace(/[^a-z0-9]/gi, '').toLowerCase();
        if (!(category in combinedFilters)) {
          combinedFilters[category] = [];
        }
        combinedFilters[category].push(toCamelCase(getFilterName(filter)));
      });
    }
  });
  for (const key in combinedFilters) {
    combinedFilters[key] = combinedFilters[key].sort((a, b) => a.localeCompare(b)).join('/');
  }
  return combinedFilters;
};

const getOmnitureFilterLocation = appliedLocations => {
  const appliedFilters = Object.values(appliedLocations)
    .filter(value => Array.isArray(value))
    .flatMap(location =>
      location.map(loc => getFilterStringOmniture({filterArr: [loc], key: 'Location'})),
    );

  return appliedFilters.length === 0 ? '' : 'Filters_Location - ' + appliedFilters.join(':');
};

export const getOmnitureObject = (appliedFilters, appliedLocations) => {
  const combinedFilters =
    appliedFilters && typeof appliedFilters === 'object'
      ? getOmnitureCombinedFilters(appliedFilters)
      : {};

  const omnitureObj = {...combinedFilters};

  if (typeof appliedLocations === 'object') {
    if (Object.keys(appliedLocations).length > 2) {
      omnitureObj.location = toCamelCase(getOmnitureFilterLocation(appliedLocations));
    }
  }

  omnitureObj.appliedFiltersList = toCamelCase(getOmnitureAppliedFilter(appliedFilters));
  return omnitureObj;
};

export const parseLocationsData = (filterData?: string) => {
  const appliedFilters = {};

  if (filterData) {
    const filterGroups = filterData?.split('^');

    filterGroups?.forEach(groupString => {
      const groupParts = groupString?.split('|');
      const filterName = groupParts?.[0];
      const filterValues = groupParts?.[1]?.split(',');
      const isRangeFilter = groupParts?.[2] === 'R';

      const filters = isRangeFilter
        ? filterValues?.map(rangeString => {
            const [min, max] = rangeString?.split('-')?.map(Number);
            return {min, max};
          })
        : filterValues?.map(value => ({value: value?.trim()}));

      appliedFilters[filterName] = filters;
    });
  }
  return appliedFilters;
};

export const convertAppliedLocationstoStr = (filterObject: any) => {
  const appliedFilters: string[] = [];
  Object.keys(filterObject).forEach(key => {
    if (filterObject[key]?.length > 0) {
      const appliedFilterValue = getFilterString({filterArr: filterObject[key], key});
      appliedFilters.push(appliedFilterValue);
    }
  });
  return appliedFilters?.join('^');
};

export const convertToNewLocationFilter = locationSearch => {
  if (!locationSearch?.length) {
    return '';
  }

  return locationSearch?.reduce((acc, currentVal) => {
    const accString = !!acc ? `${acc}^|` : '';
    return (
      accString +
      `${currentVal?.mmType}|${currentVal?.name || currentVal?.n?.split(',')?.[0]}$${currentVal?.mmId}$${currentVal?.lat}$${currentVal?.lng}|L`
    );
  }, '');
};
